import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  AlgoliaKeywordSearch,
  AlgoliaLocationSearch,
} from '../../../../components';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import { createResourceLocatorString } from '../../../../util/routes';
import { debounce } from 'lodash';
import IconCollection from '../../../../components/IconCollection/IconCollection';
import guaranteeImage from '../../../../assets/guarantee 2.png'
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import AlgoliaFilters from '../FilterComponent/AlgoliaFilters';
import css from './AlgoliaSearchSection.module.css';


const MODAL_BREAKPOINT = 768;
const isWindowDefined = typeof window !== 'undefined';
const isDesktopLayout = isWindowDefined && window.innerWidth > MODAL_BREAKPOINT;


const filters = [{ attribute: 'publicData.category', label: 'Category' }];

const moreFilters = [
  { attribute: 'publicData.access_to_rentals', label: 'Access' },
  { attribute: 'publicData.Condition_of_the_item', label: 'Condition' },
  {
    attribute: 'publicData.Trust_and_verification',
    label: 'Trust',
  },
  { attribute: 'publicData.cancelation_policy', label: 'Cancellation' },
  { attribute: 'publicData.reason_for_listing', label: 'Reason' },
  { attribute: 'publicData.security_deposit', label: 'Deposit' },
];

/**
 * Returns a new object without the specified properties.
 *
 * @param {Object} obj - The original object.
 * @param {string[]} keysToOmit - Array of property names to omit.
 * @returns {Object} A new object without the specified properties.
 */
function omitProperties(obj, keysToOmit) {
  // Create a new object excluding the properties in keysToOmit
  return Object.keys(obj).reduce((result, key) => {
    if (!keysToOmit.includes(key)) {
      result[key] = obj[key];
    }
    return result;
  }, {});
}

const getInitialValue = (attribute, params, options = []) => {
  const exactAttr = !!attribute ? attribute : null;
  // If the attribute is price.amount, return the price range
  if (exactAttr === 'price.amount' && params['priceRange']?.split(',').length > 1) {
    const min = +params['priceRange'].split(',')[0] / 100;
    const max = +params['priceRange'].split(',')[1] / 100;
    return [min, max];
  }
  const value =
    exactAttr && typeof params == 'object' ? params[exactAttr] : null;
  const option = options.find(o => o.value == value);
  return option;
};

const SearchSection = props => {
  const {
    currentUserLocationInfo,
    routesConfiguration,
    currentUser,
    history,
    currentUserCurrency,
    exchangeRates,
    config,
    intl,
    onBoundsChange,
    queryParams,
    selectedBounds,
    onActivateListing = { onActivateListing },
    algoliaListings,
    algoliaStores,
    algoliaQueryInProgress,
    facets,
    facetsError,
    facetsInProgress,
    showMap
  } = props;

  const selectedMoreFilters = Object.keys(facets).reduce((acc, key) => {
    let value = getInitialValue(key, queryParams, facets[key]) || null;
    if (value) acc[key] = value;
    return acc;
  }, {});

  const [isOpenGuarantee, setIsOpenGuarantee] = useState(false);
  const filterContainerRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isOpen, setIsOpen] = useState(false);


  useEffect(() => {
    const handleScroll = debounce(() => {
      if (filterContainerRef.current) {
        const offsetTop = filterContainerRef.current.getBoundingClientRect().top;
        if (offsetTop <= 60) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      }
    }, 100); // Adjust the debounce delay as needed

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const { keyword: qKeyword, bounds: qBounds, address: qAddress } =
    queryParams || {};

  const handleRefineLocation = (location) => {
    if (
      location &&
      location.address &&
      location.bounds &&
      (!history.location.state?.fromLandingPage || !location.fromLandingPage)
    ) {
      delete location?.isLandingPage;
      const updatedParams = { ...queryParams, ...location };
      return history.push(
        createResourceLocatorString(
          'SearchPage',
          routesConfiguration,
          {},
          updatedParams
        )
      );
    }
    return null;
  };

  const handleRefineKeyword = (keyword = '') => {
    const updatedParams = { ...queryParams, keyword: keyword || '' };
    history.push(
      createResourceLocatorString(
        'SearchPage',
        routesConfiguration,
        {},
        updatedParams
      )
    );
  };


  const handleShowRentals = (selectedFilters) => {
    const updatedParams = { ...(queryParams || {}) };

    Object.entries(selectedFilters).forEach(([attribute, values]) => {
      if (attribute === 'priceRange' && Array.isArray(values)) {
        if (values[0] != 0 || values[1] != 10000) {
          updatedParams['priceRange'] = `${values[0] * 100},${values[1] * 100}`; // convert to "min,max"
        } else {
          delete updatedParams['priceRange']
        }
      } else if (typeof values === 'string' || typeof values === 'number') {
        updatedParams[attribute] = values;
      }
    });

    history.push(
      createResourceLocatorString('SearchPage', routesConfiguration, {}, updatedParams)
    );

    setIsOpen(false);
  };



  const handleClearFilters = () => {
    // Remove all the filters.
    const filteredParams = omitProperties(
      queryParams,
      [...[...filters, ...moreFilters].map(f => f.attribute.split('.')[1]), 'priceRange']
    );


    history.push(
      createResourceLocatorString(
        'SearchPage',
        routesConfiguration,
        {},
        filteredParams
      )
    );
  };

  return (
    <>
      {/* Search Inputs */}
      <div ref={filterContainerRef} className={classNames(css.searchInputs, isSticky && css.stickyBorder)}>
        <AlgoliaLocationSearch
          attribute={'publicData.location.address'}
          defaultLocation={currentUserLocationInfo}
          onRefineLocation={useCallback(l => handleRefineLocation(l), [
            queryParams,
          ])}
          initialValues={{ bounds: qBounds, address: qAddress }}
        />

        <AlgoliaKeywordSearch
          onSearchChange={useCallback(k => handleRefineKeyword(k), [
            queryParams,
          ])}
          initialValue={{ keyword: qKeyword }}
          history={history}
          currentUserCurrency={currentUserCurrency}
          currentUser={currentUser}
          exchangeRates={exchangeRates}
          routes={routesConfiguration}
          config={config}
          intl={intl}
          clearKeywordsClass={css.clearKeywords}
          autoFocus={true}
          selectedBounds={selectedBounds}
          onActivateListing={onActivateListing}
          searchInputText={intl.formatMessage({ id: isDesktopLayout ? "AlgoliaKeywordSearch.placeholder" : "AlgoliaKeywordSearch.placeholderMobile" })}
          algoliaListings={algoliaListings}
          algoliaStores={algoliaStores}
          algoliaQueryInProgress={algoliaQueryInProgress}
          showMap={showMap}
        />
      </div>

      {/* Filters */}
      <div className={css.desktopFilters}>
        {!facetsError ? (
          <div className={css.filterWrapper}>
            <div className={css.guaranteeWrapper}>
              <img src={guaranteeImage} alt="" />
              <span className={css.guarantee}>
                <FormattedMessage id="AlgoliaSearchPage.guaranteeCover" /> <br /> <span onClick={() => setIsOpenGuarantee(true)} className={css.underlineText}><FormattedMessage id="AlgoliaSearchPage.guaranteeMoney" /></span>
              </span>

              {isOpenGuarantee && (
                <div className={css.popupOverlay}>
                  <div className={css.popupContentWrapper}>
                    <div className={css.popupContent}>
                      <button className={css.popupCloseButton} onClick={() => setIsOpenGuarantee(false)}>
                        &times;
                      </button>
                      <h2 className={css.popupHeader}><FormattedMessage id="AlgoliaSearchPage.guarantee" /></h2>
                      <p><FormattedMessage id="AlgoliaSearchPage.guaranteeSummary" values={{ renterProgram: <span className={css.boldText}>{intl.formatMessage({ id: 'AlgoliaSearchPage.renterProgram' })}</span>, guaranteeMoney: <span className={css.boldText}>{intl.formatMessage({ id: 'AlgoliaSearchPage.guaranteeMoney' })}</span> }} /></p>
                      <div className={css.popupBody}>
                        <p><FormattedMessage id="AlgoliaSearchPage.guaranteeImbursement" /></p>
                        <ul className={css.popupList}>
                          <li><FormattedMessage id="AlgoliaSearchPage.guaranteeApproved" values={{ withinHours: <span className={css.boldText}>{intl.formatMessage({ id: 'AlgoliaSearchPage.withinHours' })}</span> }} /></li>
                          <li><FormattedMessage id="AlgoliaSearchPage.guaranteeRental" values={{ itemUnavailable: <span className={css.boldText}>{intl.formatMessage({ id: 'AlgoliaSearchPage.itemUnavailable' })}</span> }} /></li>
                          <li><FormattedMessage id="AlgoliaSearchPage.guaranteeDescription" values={{ descriptionMatch: <span className={css.boldText}>{intl.formatMessage({ id: 'AlgoliaSearchPage.descriptionMatch' })}</span> }} /></li>
                          <li><FormattedMessage id="AlgoliaSearchPage.guaranteeCondition" values={{ itemWorking: <span className={css.boldText}>{intl.formatMessage({ id: 'AlgoliaSearchPage.itemWorking' })}</span> }} /></li>
                        </ul>
                      </div>
                      <p><FormattedMessage id="AlgoliaSearchPage.guaranteeCreditSummary" values={{ guaranteeCredit: <span className={css.boldText}>{intl.formatMessage({ id: 'AlgoliaSearchPage.guaranteeCredit' })}</span> }} /></p>
                      <p><FormattedMessage id="AlgoliaSearchPage.guaranteePaymentSummary" values={{ guaranteePayment: <span className={css.boldText}>{intl.formatMessage({ id: 'AlgoliaSearchPage.guaranteePayment' })}</span>, stripe: <span className={css.boldText}>{intl.formatMessage({ id: 'AlgoliaSearchPage.stripe' })}</span> }} /></p>
                      <p><FormattedMessage id="AlgoliaSearchPage.guaranteeCommit" /></p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <AlgoliaFilters
              facets={facets}
              manageDisableScrolling={manageDisableScrolling}
              handleShowRentals={handleShowRentals}
              setIsOpen={setIsOpen}
              isOpen={isOpen}
              selectedFilters={selectedMoreFilters}
              showResetButton={Object.keys(selectedMoreFilters).length > 0}
              onResetFilters={handleClearFilters}
            />
          </div>
        ) : facetsError && !facetsInProgress
          ? <p className={css.error}>Failed to fetch filters, please try again.</p>
          : null}
      </div>
    </>
  );
};

export default SearchSection;
